/**
 * PublicLivePOBPage — shown when an employee scans their OWN badge QR.
 * URL: https://<app>/?e=<empQrHash>
 * No login. Employee is identified from the QR hash. They report SAFE or NEED HELP.
 */
import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, Stethoscope, Flame, Lock, HelpCircle, Shield, MoreHorizontal, Clock, ShieldCheck, FileWarning, ArrowLeft } from 'lucide-react';
import IncidentReportForm from '../components/IncidentReportForm';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const S = {
  bg: '#0a0e17', surface: '#131a2b', elevated: '#1a2235',
  border: 'rgba(255,255,255,0.08)', text: '#e2e8f0', muted: '#64748b',
  subtle: '#94a3b8', green: '#22c55e', red: '#ef4444', blue: '#3b82f6',
  amber: '#f59e0b', orange: '#f97316', pink: '#ec4899', purple: '#a78bfa',
};

export default function PublicLivePOBPage({ empHash }) {
  const [phase, setPhase] = useState('loading'); // loading | action | emergency | other | done | error
  const [otherFunction, setOtherFunction] = useState(null); // null | 'incident'   (TA flow is gated by taPhase within the 'other' phase)
  const [employee, setEmployee] = useState(null);
  const [gps, setGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [photoModal, setPhotoModal] = useState(false);
  const [taStatus, setTaStatus] = useState(null);   // null | { taEnabled, openSession }
  const [taPhase, setTaPhase] = useState('idle');    // idle | selfie | submitting | match_confirmed | mismatch_warning | done | error
  const [taResult, setTaResult] = useState(null);
  const [taError, setTaError] = useState('');
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [selfieStatus, setSelfieStatus] = useState(null);
  const [selfieUploadedUrl, setSelfieUploadedUrl] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');

  // Load employee info + start GPS capture simultaneously
  useEffect(() => {
    fetch(`${API}/api/public/employee/${empHash}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setError(data.error); setPhase('error'); return; }
        setEmployee(data);
        setPhase('action');
        fetch(`${API}/api/public/ta/status/${empHash}`)
          .then(r => r.json())
          .then(ts => { if (!ts.error) setTaStatus(ts); })
          .catch(() => {}); // TA is optional, swallow errors
        // Start GPS capture silently as soon as the page loads
        if (navigator.geolocation) {
          setGpsLoading(true);
          navigator.geolocation.getCurrentPosition(
            pos => { setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGpsLoading(false); },
            () => setGpsLoading(false),
            { timeout: 8000 }
          );
        }
      })
      .catch(() => { setError('Unable to reach the server. Check your connection.'); setPhase('error'); });
  }, [empHash]);

  function handleTaClockIn() {
    if (taStatus?.selfieRequired) {
      setTaPhase('selfie');
    } else {
      submitClockIn(null);
    }
  }

  function handleSelfieChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setSelfieFile(file);
    setSelfiePreview(URL.createObjectURL(file));
  }

  async function submitClockIn(selfieUrl, selfieStatusParam) {
    setTaPhase('submitting');
    setTaError('');
    try {
      const body = {
        qrHash: empHash,
        gps_lat: gps?.lat || null,
        gps_lng: gps?.lng || null,
        selfieUrl: selfieUrl || null,
        selfieStatus: selfieStatusParam || selfieStatus || null,
        reason: overrideReason || null,
      };
      const r = await fetch(`${API}/api/public/ta/clock-in`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (data.error) {
        if (data.geofenceStatus === 'OUTSIDE_BLOCKED') {
          setTaError('Clock-in blocked: you are outside the geofence.');
          setTaPhase('error');
        } else if (data.error === 'A reason is required when outside the geofence.') {
          setTaPhase('reason_required');
        } else {
          setTaError(data.error);
          setTaPhase('error');
        }
        return;
      }
      setTaResult({ type: 'in', sessionId: data.sessionId });
      setTaStatus(prev => ({ ...prev, openSession: { id: data.sessionId } }));
      setTaPhase('done');
    } catch {
      setTaError('Network error. Please try again.');
      setTaPhase('error');
    }
  }

  async function handleTaClockOut() {
    setTaPhase('submitting');
    setTaError('');
    try {
      const r = await fetch(`${API}/api/public/ta/clock-out`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qrHash: empHash, gps_lat: gps?.lat || null, gps_lng: gps?.lng || null }),
      });
      const data = await r.json();
      if (data.error) { setTaError(data.error); setTaPhase('error'); return; }
      setTaResult({ type: 'out', hoursWorked: data.hoursWorked });
      setTaStatus(prev => ({ ...prev, openSession: null }));
      setTaPhase('done');
    } catch {
      setTaError('Network error. Please try again.');
      setTaPhase('error');
    }
  }

  async function handleSelfieSubmit() {
    if (!selfieFile) return;
    setTaPhase('submitting');
    try {
      const fd = new FormData();
      fd.append('selfie', selfieFile);
      fd.append('qrHash', empHash);
      const r = await fetch(`${API}/api/public/ta/upload-selfie`, { method: 'POST', body: fd });
      const data = await r.json();
      if (data.error) { setTaError(data.error); setTaPhase('error'); return; }
      setSelfieStatus(data.selfieStatus);
      if (data.selfieStatus === 'NO_MATCH') {
        setSelfieUploadedUrl(data.selfieUrl);
        setTaPhase('mismatch_warning');
      } else if (data.selfieStatus === 'MATCH') {
        setSelfieUploadedUrl(data.selfieUrl);
        setTaPhase('match_confirmed');
        setTimeout(() => submitClockIn(data.selfieUrl, 'MATCH'), 1500);
      } else {
        // SKIPPED or ERROR — proceed silently
        await submitClockIn(data.selfieUrl, data.selfieStatus);
      }
    } catch {
      setTaError('Selfie upload failed.');
      setTaPhase('error');
    }
  }

  function handleSafe() {
    submit('SAFE', null);
  }

  function handleOtherBack() {
    // Coming back from the Incident form → just close it, stay on Other menu
    if (otherFunction === 'incident') { setOtherFunction(null); return; }
    // Mid TA sub-flow → cancel the sub-flow and stay on Other menu (taStatus is preserved)
    if (taPhase && taPhase !== 'idle' && taPhase !== 'done') {
      setTaPhase('idle');
      setSelfieFile(null); setSelfiePreview(null);
      setSelfieUploadedUrl(null); setSelfieStatus(null);
      setOverrideReason(''); setTaError('');
      return;
    }
    // Otherwise → return to the SAFE / NEED HELP screen
    setPhase('action');
  }

  function handleEmergencyType(type) {
    submit('NEED_HELP', type);
  }

  function submit(action, emergencyType) {
    setSubmitting(true);
    fetch(`${API}/api/public/livepob`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ empQrHash: empHash, action, emergencyType: emergencyType || undefined, gps: gps || undefined }),
    })
      .then(r => r.json())
      .then(data => {
        setSubmitting(false);
        if (data.error) { setError(data.error); return; }
        setResult({ ...data, action });
        setPhase('done');
      })
      .catch(() => { setSubmitting(false); setError('Network error. Please try again.'); });
  }

  const page = {
    minHeight: '100vh', background: S.bg, display: 'flex', flexDirection: 'column',
    alignItems: 'center', padding: '0 0 40px',
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  };
  const header = {
    width: '100%', background: S.surface, borderBottom: `1px solid ${S.border}`,
    padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px',
  };
  const card = { width: '100%', maxWidth: '420px', padding: '0 20px' };

  if (phase === 'loading') return (
    <div style={page}>
      <div style={{ marginTop: '40vh', color: S.muted, fontSize: '14px', textAlign: 'center' }}>
        <Shield size={32} color={S.blue} style={{ display: 'block', margin: '0 auto 12px' }} />
        Loading…
      </div>
    </div>
  );

  if (phase === 'error') return (
    <div style={page}>
      <div style={{ marginTop: '30vh', padding: '32px', textAlign: 'center', maxWidth: '360px' }}>
        <AlertTriangle size={40} color={S.amber} style={{ display: 'block', margin: '0 auto 16px' }} />
        <div style={{ fontSize: '16px', fontWeight: 700, color: S.text, marginBottom: '8px' }}>Error</div>
        <div style={{ fontSize: '14px', color: S.subtle }}>{error}</div>
      </div>
    </div>
  );

  return (
    <div style={page}>
      <div style={header}>
        <Shield size={20} color={S.blue} />
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: S.text }}>SafeCheck</div>
          <div style={{ fontSize: '11px', color: S.muted }}>LivePOB — Report Status</div>
        </div>
        {gpsLoading && <div style={{ marginLeft: 'auto', fontSize: '11px', color: S.blue }}>📍 Getting GPS…</div>}
        {gps && !gpsLoading && <div style={{ marginLeft: 'auto', fontSize: '11px', color: S.green }}>📍 GPS ready</div>}
      </div>

      <div style={card}>
        {/* Employee greeting */}
        {employee && (
          <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: '14px', padding: '16px 18px', marginBottom: '28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '12px', color: S.muted, marginBottom: '4px' }}>Hello,</div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: S.text, marginBottom: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{employee.name}</div>
              <div style={{ fontSize: '12px', color: S.subtle }}>{employee.employeeId} · {employee.department}</div>
            </div>
            {employee.photoUrl
              ? <img src={employee.photoUrl} alt={employee.name} onClick={() => setPhotoModal(true)} style={{ width: '58px', height: '58px', borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', flexShrink: 0, border: `2px solid ${S.border}`, cursor: 'pointer' }} />
              : <div style={{ width: '58px', height: '58px', borderRadius: '50%', background: S.elevated, border: `2px solid ${S.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '22px', fontWeight: 700, color: S.muted }}>
                  {employee.name.charAt(0).toUpperCase()}
                </div>
            }

            {/* Full-size photo lightbox */}
            {photoModal && employee.photoUrl && (
              <div onClick={() => setPhotoModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
                <img src={employee.photoUrl} alt={employee.name} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '12px', objectFit: 'contain', objectPosition: 'top', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }} />
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{ background: S.red+'22', border: `1px solid ${S.red}44`, borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px', color: S.red }}>
            {error}
          </div>
        )}

        {/* Action phase — primary panic-button screen */}
        {phase === 'action' && (() => {
          const hasOtherFunctions =
            taStatus?.taEnabled ||
            employee?.hseProfiler ||
            employee?.incidentReportingEnabled;
          return (
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: S.text, marginBottom: '20px', textAlign: 'center' }}>
                What is your current status?
              </div>

              <button
                onClick={handleSafe}
                disabled={submitting}
                style={{ width: '100%', padding: '24px 16px', borderRadius: '16px', border: 'none', background: S.green, color: '#fff', fontSize: '20px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', marginBottom: '14px', boxShadow: `0 6px 24px ${S.green}44` }}
              >
                <CheckCircle size={30} />
                I AM SAFE
              </button>

              <button
                onClick={() => setPhase('emergency')}
                disabled={submitting}
                style={{ width: '100%', padding: '24px 16px', borderRadius: '16px', border: 'none', background: S.red, color: '#fff', fontSize: '20px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', boxShadow: `0 6px 24px ${S.red}44` }}
              >
                <AlertTriangle size={30} />
                I NEED HELP
              </button>

              {hasOtherFunctions && (
                <button
                  onClick={() => { setOtherFunction(null); setPhase('other'); }}
                  disabled={submitting}
                  style={{ width: '100%', padding: '18px 16px', borderRadius: '14px', border: `1px solid ${S.border}`, background: S.surface, color: S.text, fontSize: '16px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '14px' }}
                >
                  <MoreHorizontal size={22} color={S.subtle} />
                  OTHER
                </button>
              )}

              {submitting && <div style={{ textAlign: 'center', color: S.muted, fontSize: '13px', marginTop: '14px' }}>Submitting…</div>}
            </div>
          );
        })()}

        {/* Other phase — menu of enabled secondary functions (TA, HSE Checker, Report Incident) */}
        {phase === 'other' && (
          <div>
            <button onClick={handleOtherBack}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: S.muted, fontSize: '14px', cursor: 'pointer', padding: '4px 0', marginBottom: '14px', fontFamily: 'inherit' }}>
              <ArrowLeft size={18} />
              Back
            </button>

            {otherFunction === 'incident' ? (
              <IncidentReportForm
                apiBase={API}
                empHash={empHash}
                gps={gps}
                onSubmitted={() => { /* form shows its own success view; user taps Back to return */ }}
                onBack={() => setOtherFunction(null)}
                S={S}
              />
            ) : (
              <>
                {/* Time & Attendance — preserved sub-flow */}
                {taStatus?.taEnabled && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: S.subtle, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Time & Attendance
                    </div>

                    {taPhase === 'match_confirmed' && (
                      <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{ fontSize: '56px', marginBottom: '12px' }}>✅</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: S.green }}>Identity Verified</div>
                        <div style={{ fontSize: '13px', color: S.muted, marginTop: '6px' }}>Clocking in…</div>
                      </div>
                    )}

                    {taPhase === 'mismatch_warning' && (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: S.amber, marginBottom: '8px' }}>Selfie Doesn't Match Profile</div>
                        <div style={{ fontSize: '13px', color: S.subtle, marginBottom: '20px', lineHeight: 1.5 }}>
                          Your selfie doesn't appear to match your profile photo. You can clock in anyway —
                          your attendance officer will be notified to review this entry.
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <button onClick={() => submitClockIn(selfieUploadedUrl, 'NO_MATCH')}
                            style={{ padding: '14px', borderRadius: '10px', border: 'none', background: S.amber, color: '#fff', fontSize: '15px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                            Clock In Anyway
                          </button>
                          <button onClick={() => { setTaPhase('selfie'); setSelfieFile(null); setSelfiePreview(null); setSelfieUploadedUrl(null); setSelfieStatus(null); }}
                            style={{ padding: '10px', borderRadius: '10px', border: `1px solid ${S.border}`, background: 'transparent', color: S.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
                            Retake Selfie
                          </button>
                        </div>
                      </div>
                    )}

                    {taPhase === 'selfie' && (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '14px', color: S.text, marginBottom: '14px' }}>Take a selfie to clock in</div>
                        {selfiePreview && <img src={selfiePreview} alt="selfie" style={{ width: '120px', height: '120px', borderRadius: '50%', objectFit: 'cover', marginBottom: '14px', border: `2px solid ${S.border}` }} />}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <label style={{ display: 'block', padding: '12px', borderRadius: '10px', border: `1px solid ${S.border}`, background: S.elevated, color: S.text, fontSize: '14px', cursor: 'pointer', textAlign: 'center' }}>
                            {selfiePreview ? 'Retake Photo' : 'Open Camera'}
                            <input type="file" accept="image/*" capture="user" style={{ display: 'none' }} onChange={handleSelfieChange} />
                          </label>
                          {selfiePreview && (
                            <button onClick={handleSelfieSubmit} style={{ padding: '14px', borderRadius: '10px', border: 'none', background: S.blue, color: '#fff', fontSize: '15px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                              Clock In
                            </button>
                          )}
                          <button onClick={() => { setTaPhase('idle'); setSelfieFile(null); setSelfiePreview(null); }} style={{ padding: '10px', borderRadius: '10px', border: `1px solid ${S.border}`, background: 'transparent', color: S.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {taPhase === 'submitting' && (
                      <div style={{ textAlign: 'center', color: S.muted, fontSize: '13px', padding: '16px' }}>Processing…</div>
                    )}

                    {taPhase === 'reason_required' && (
                      <div>
                        <div style={{ background: S.amber+'22', border: `1px solid ${S.amber}44`, borderRadius: '10px', padding: '12px 14px', marginBottom: '14px', fontSize: '13px', color: S.amber }}>
                          You are outside the geofence. Please provide a reason for your manager to review.
                        </div>
                        <textarea
                          value={overrideReason}
                          onChange={e => setOverrideReason(e.target.value)}
                          placeholder="e.g. Working from client site today"
                          rows={3}
                          style={{ width: '100%', boxSizing: 'border-box', background: S.elevated, border: `1px solid ${S.border}`, borderRadius: '10px', padding: '10px 12px', color: S.text, fontSize: '14px', fontFamily: 'inherit', outline: 'none', resize: 'vertical', marginBottom: '10px' }}
                        />
                        <button
                          onClick={() => submitClockIn(selfieUploadedUrl, selfieStatus)}
                          disabled={!overrideReason.trim()}
                          style={{ width: '100%', padding: '14px', borderRadius: '10px', border: 'none', background: overrideReason.trim() ? S.blue : S.muted, color: '#fff', fontSize: '15px', fontWeight: 700, cursor: overrideReason.trim() ? 'pointer' : 'default', fontFamily: 'inherit', marginBottom: '8px' }}>
                          Submit for Approval
                        </button>
                        <button onClick={() => { setTaPhase('idle'); setOverrideReason(''); }} style={{ width: '100%', padding: '10px', borderRadius: '10px', border: `1px solid ${S.border}`, background: 'transparent', color: S.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
                          Cancel
                        </button>
                      </div>
                    )}

                    {taPhase === 'error' && (
                      <div>
                        <div style={{ background: S.red+'22', border: `1px solid ${S.red}44`, borderRadius: '10px', padding: '12px 14px', marginBottom: '12px', fontSize: '13px', color: S.red }}>
                          {taError}
                        </div>
                        <button onClick={() => { setTaPhase('idle'); setTaError(''); }} style={{ width: '100%', padding: '10px', borderRadius: '10px', border: `1px solid ${S.border}`, background: 'transparent', color: S.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
                          Try Again
                        </button>
                      </div>
                    )}

                    {taPhase === 'done' && taResult && (
                      <div style={{ background: S.green+'22', border: `1px solid ${S.green}44`, borderRadius: '10px', padding: '14px', textAlign: 'center', fontSize: '14px', color: S.green, fontWeight: 600 }}>
                        {taResult.type === 'in' ? '✓ Clocked In' : `✓ Clocked Out · ${taResult.hoursWorked}h worked`}
                      </div>
                    )}

                    {taPhase === 'idle' && (
                      taStatus.openSession ? (
                        <button onClick={handleTaClockOut} style={{ width: '100%', padding: '18px 16px', borderRadius: '14px', border: `1px solid ${S.border}`, background: S.surface, color: S.text, fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Clock size={22} color={S.amber} />
                          <div style={{ textAlign: 'left' }}>
                            <div>Clock Out</div>
                            <div style={{ fontSize: '11px', color: S.muted, marginTop: '2px' }}>End your shift</div>
                          </div>
                        </button>
                      ) : (
                        <button onClick={handleTaClockIn} style={{ width: '100%', padding: '18px 16px', borderRadius: '14px', border: `1px solid ${S.border}`, background: S.surface, color: S.text, fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Clock size={22} color={S.blue} />
                          <div style={{ textAlign: 'left' }}>
                            <div>Clock In</div>
                            <div style={{ fontSize: '11px', color: S.muted, marginTop: '2px' }}>Start your shift</div>
                          </div>
                        </button>
                      )
                    )}
                  </div>
                )}

                {/* HSE Checker — only for HSE officers (hse_profiler = true) */}
                {employee?.hseProfiler && taPhase === 'idle' && (
                  <button onClick={() => { window.location.href = '/?hse=1'; }}
                    style={{ width: '100%', padding: '18px 16px', borderRadius: '14px', border: `1px solid ${S.border}`, background: S.surface, color: S.text, fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <ShieldCheck size={22} color={S.blue} />
                    <div style={{ textAlign: 'left' }}>
                      <div>HSE Checker</div>
                      <div style={{ fontSize: '11px', color: S.muted, marginTop: '2px' }}>Scan an employee badge for HSE training status</div>
                    </div>
                  </button>
                )}

                {/* Report Incident — opens the inline IncidentReportForm */}
                {employee?.incidentReportingEnabled && taPhase === 'idle' && (
                  <button onClick={() => setOtherFunction('incident')}
                    style={{ width: '100%', padding: '18px 16px', borderRadius: '14px', border: `1px solid ${S.border}`, background: S.surface, color: S.text, fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <FileWarning size={22} color={S.amber} />
                    <div style={{ textAlign: 'left' }}>
                      <div>Report Incident</div>
                      <div style={{ fontSize: '11px', color: S.muted, marginTop: '2px' }}>Injury, near-miss, property damage…</div>
                    </div>
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Emergency type selection */}
        {phase === 'emergency' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <AlertTriangle size={48} color={S.red} style={{ display: 'block', margin: '0 auto 12px' }} />
              <div style={{ fontSize: '22px', fontWeight: 700, color: S.red, marginBottom: '6px' }}>Emergency Alert</div>
              <div style={{ fontSize: '14px', color: S.subtle }}>Select the type of emergency</div>
              {gpsLoading && <div style={{ fontSize: '12px', color: S.blue, marginTop: '8px' }}>Capturing your GPS location…</div>}
              {gps && <div style={{ fontSize: '11px', color: S.green, marginTop: '6px', fontFamily: 'JetBrains Mono, monospace' }}>GPS: {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}</div>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              {[
                ['MEDICAL', 'Medical', S.pink,   Stethoscope],
                ['FIRE',    'Fire',    S.orange,  Flame],
                ['SECURITY','Security',S.purple,  Lock],
                ['OTHER',   'Other',   S.amber,   HelpCircle],
              ].map(([type, label, color, Icon]) => (
                <button key={type} onClick={() => handleEmergencyType(type)} disabled={submitting}
                  style={{ padding: '26px 10px', borderRadius: '16px', border: `2px solid ${color}`, background: color+'22', color, fontSize: '16px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  <Icon size={32} />
                  {label}
                </button>
              ))}
            </div>

            <button onClick={() => setPhase('action')} style={{ width: '100%', marginTop: '16px', padding: '12px', borderRadius: '10px', border: `1px solid ${S.border}`, background: 'transparent', color: S.muted, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}>
              ← Back
            </button>

            {submitting && <div style={{ textAlign: 'center', color: S.muted, fontSize: '13px', marginTop: '14px' }}>Sending alert…</div>}
          </div>
        )}

        {/* Done */}
        {phase === 'done' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CheckCircle size={72} color={result?.action === 'NEED_HELP' ? S.red : S.green} style={{ display: 'block', margin: '0 auto 20px' }} />
            <div style={{ fontSize: '24px', fontWeight: 700, color: S.text, marginBottom: '10px' }}>
              {result?.action === 'NEED_HELP' ? '🚨 Alert Sent' : '✓ Marked Safe'}
            </div>
            <div style={{ fontSize: '15px', color: S.subtle, marginBottom: '6px' }}>{result?.employeeName}</div>

            {result?.action === 'NEED_HELP' && (
              <div style={{ background: S.red+'22', border: `1px solid ${S.red}44`, borderRadius: '12px', padding: '16px', margin: '20px 0', fontSize: '14px', color: S.red, fontWeight: 600, lineHeight: 1.5 }}>
                Your emergency alert has been sent to the safety team.<br />Stay in place if possible and await assistance.
              </div>
            )}

            {result?.action === 'SAFE' && (
              <div style={{ background: S.green+'22', border: `1px solid ${S.green}44`, borderRadius: '12px', padding: '16px', margin: '20px 0', fontSize: '14px', color: S.green, fontWeight: 600 }}>
                Your status has been recorded. Stay safe.
              </div>
            )}

            <button onClick={() => { setPhase('action'); setResult(null); setError(''); }}
              style={{ padding: '14px 36px', borderRadius: '10px', border: `1px solid ${S.border}`, background: S.elevated, color: S.subtle, fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Report Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
